import React from 'react';
import {
    ProductVariant,
    useDeleteProductVariant,
    ResourceType,
} from '@travelity/api';
import {
    Card,
    ConfirmationDialog,
    IconButton,
    Tag,
    IconButtonGroup,
} from '@travelity/ui';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TagIcon from '@mui/icons-material/Tag';
import { copyToClipboard } from '@travelity/utils/clipboard';
import { ProductVariantCardSkeleton } from './product-variant-card-skeleton';
import { useResourcePermissions } from '../../../contexts/user';
import { useToast } from '../../../hooks';

export interface ProductVariantCardProps {
    variant: ProductVariant;
    onDelete: () => void;
}

export const ProductVariantCard = (props: ProductVariantCardProps) => {
    const { variant, onDelete } = props;
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const { t } = useTranslation();
    const { showError, showSuccess } = useToast();
    const { mutate: deleteProductVariant, isLoading: isDeleting } =
        useDeleteProductVariant({
            onSuccess: () => {
                setShowDeleteModal(false);
                onDelete();
                showSuccess('DELETE_PRODUCT_VARIANT_SUCCESS', {
                    name: variant.productName,
                });
            },
            onError: () => {
                showError('DELETE_PRODUCT_VARIANT_FAILURE', {
                    name: variant.productName,
                });
            },
        });
    const permissions = useResourcePermissions(ResourceType.PRODUCT_VARIANT);

    const handleCopy = async () => {
        try {
            await copyToClipboard(variant.id);
            showSuccess('ID_COPIED_SUCCESS');
        } catch (error) {
            console.error('Failed to copy URL', error);
        }
    };

    return (
        <>
            <Card
                buttons={
                    <>
                        <IconButtonGroup>
                            <IconButton
                                icon={<TagIcon fontSize="small" />}
                                onClick={handleCopy}
                                tooltip="Copy ID"
                            />
                        </IconButtonGroup>
                        <IconButtonGroup>
                            <IconButton
                                hoverColor="error.main"
                                icon={<DeleteIcon fontSize="small" />}
                                onClick={() => setShowDeleteModal(true)}
                                disabled={isDeleting || !permissions.delete}
                                tooltip="Delete"
                            />
                        </IconButtonGroup>
                    </>
                }
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        px: 2,
                        height: '46px',
                        py: '10px',
                        bgcolor: '#F4F6FA',
                        borderRadius: '12px 0px 0px 0px',
                    }}
                >
                    <Box
                        component="span"
                        title={variant.productName}
                        sx={{
                            color: '#8F97AA',
                            pr: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {variant.productName}
                    </Box>
                    <Stack direction="row" gap={1}>
                        <Tag
                            label={t('Type')}
                            values={[t(variant.productType, { ns: 'product' })]}
                        />
                        <Tag
                            label={t('Capacity')}
                            values={[t(variant.capacityName)]}
                        />
                    </Stack>
                </Stack>
                <Stack
                    direction="row"
                    gap={1}
                    p={2}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography
                        sx={{
                            color: '#B7BCC7',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Product Options
                    </Typography>
                    <Stack direction="row" gap={0.5}>
                        {variant.options.map(option => (
                            <Tag key={option.name} values={[option.name]} />
                        ))}
                    </Stack>
                </Stack>
            </Card>
            <ConfirmationDialog
                title={t(
                    'Are you sure you want to delete this product variant?'
                )}
                content={t(
                    'This will delete this product variant permanently.'
                )}
                isLoading={isDeleting}
                confirmText={t('Delete')}
                open={showDeleteModal}
                handleCancel={() => setShowDeleteModal(false)}
                handleConfirm={() => deleteProductVariant({ id: variant.id })}
            />
        </>
    );
};

ProductVariantCard.Skeleton = ProductVariantCardSkeleton;
